// 1. React
import React, { useState, useEffect, useCallback } from 'react';

// 2. Third-party
import { useParams, Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Card, Button, Spin, Result, Tag, Descriptions, Alert, Progress, message } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  FormOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import moment from 'moment';

// 3. Components / constants / services
import LayoutWrapper from '@iso/components/utility/layoutWrapper';
import SchemaFormRenderer from '@iso/components/SchemaFormRenderer';
import { educationSessionService } from '@iso/services';
import TrainingSessionRegisterWrapper from './TrainingSessionRegister.styles';

// Where the attendee is in the scan flow. Kept as one value rather than a pile
// of booleans so the screen can never show two phases at once.
const PHASE = {
  LOADING: 'loading',
  ERROR: 'error',
  CONFIRM: 'confirm',
  ASSESSMENT: 'assessment',
  DONE: 'done',
};

const formatTimeRange = (start, finish) => {
  const from = start ? moment(start, ['HH:mm:ss', 'YYYY-MM-DD HH:mm:ss']).format('HH:mm') : null;
  const to = finish ? moment(finish, ['HH:mm:ss', 'YYYY-MM-DD HH:mm:ss']).format('HH:mm') : null;
  if (from && to) return `${from} – ${to}`;
  return from || '—';
};

/**
 * The page a training QR code opens.
 *
 * Scan -> confirm what you're joining -> register -> (if the session has an
 * assessment) sit it -> see your result. Everything here is deliberately
 * single-purpose and phone-shaped: someone is standing in a shed holding a
 * phone, not sitting at a desk.
 *
 * Registration is idempotent server-side, so re-scanning is safe and simply
 * lands the attendee wherever they left off.
 */
const TrainingSessionRegister = () => {
  const { id: sessionId } = useParams();
  const isLoggedIn = useSelector((state) => state.Auth.idToken);

  const [phase, setPhase] = useState(PHASE.LOADING);
  const [errorMessage, setErrorMessage] = useState('');
  const [session, setSession] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [myRegistration, setMyRegistration] = useState(null);
  const [assessmentForm, setAssessmentForm] = useState(null);
  const [previousAttempt, setPreviousAttempt] = useState(null);
  const [scoring, setScoring] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadSession = useCallback(async () => {
    setPhase(PHASE.LOADING);
    try {
      const res = await educationSessionService.getQrInfo(sessionId);
      if (res.code !== '0000') {
        setErrorMessage(res.message || 'This training session could not be found.');
        setPhase(PHASE.ERROR);
        return;
      }
      setSession(res.session);
      setAssessment(res.session.assessment || null);
      setMyRegistration(res.session.my_registration || null);
      setPhase(PHASE.CONFIRM);
    } catch (e) {
      setErrorMessage('We could not reach the server. Check your connection and try again.');
      setPhase(PHASE.ERROR);
    }
  }, [sessionId]);

  useEffect(() => {
    if (isLoggedIn && sessionId) {
      loadSession();
    }
  }, [isLoggedIn, sessionId, loadSession]);

  // Pull the assessment paper. The server refuses this until the caller is on
  // the attendee list, which is why it always runs AFTER registration.
  const openAssessment = async () => {
    setBusy(true);
    try {
      const res = await educationSessionService.getAssessment(sessionId);
      if (res.code !== '0000') {
        message.error(res.message || 'The assessment could not be opened.');
        return;
      }
      setAssessmentForm(res.form);
      setPreviousAttempt(res.my_attempt || null);
      setPhase(PHASE.ASSESSMENT);
    } finally {
      setBusy(false);
    }
  };

  const handleRegister = async () => {
    setBusy(true);
    try {
      const res = await educationSessionService.registerAttendee(sessionId);
      if (res.code !== '0000') {
        message.error(res.message || 'You could not be registered for this session.');
        return;
      }
      message.success(res.message);
      setMyRegistration({ ...(myRegistration || {}), registered: true });

      if (assessment && assessment.has_assessment) {
        await openAssessment();
      } else {
        setPhase(PHASE.DONE);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleSubmitAssessment = async (answers) => {
    setBusy(true);
    try {
      const res = await educationSessionService.submitAssessment(sessionId, answers);
      if (res.code !== '0000') {
        // Validation messages ("complete every required question") come back
        // here — show them and keep the attendee on the paper.
        message.error(res.message || 'Your answers could not be submitted.');
        return;
      }
      setScoring(res.scoring || null);
      setPhase(PHASE.DONE);
    } finally {
      setBusy(false);
    }
  };

  // Not signed in: bounce to sign-in carrying this page as the destination, so
  // the scan survives the login instead of dumping them on the dashboard.
  if (!isLoggedIn) {
    const destination = `/training-session/register/${sessionId}`;
    return <Redirect to={`/signin?redirect=${encodeURIComponent(destination)}`} />;
  }

  const renderSessionCard = () => (
    <Descriptions column={1} size="small" bordered className="session-facts">
      <Descriptions.Item label="Date">
        {session.session_date ? moment(session.session_date).format('DD/MM/YYYY') : '—'}
      </Descriptions.Item>
      <Descriptions.Item label="Time">
        {formatTimeRange(session.start_time, session.finish_time)}
      </Descriptions.Item>
      <Descriptions.Item label="Location">{session.location_name || '—'}</Descriptions.Item>
      <Descriptions.Item label="Trainer">{session.trainer_name || '—'}</Descriptions.Item>
    </Descriptions>
  );

  const renderConfirm = () => {
    const alreadyRegistered = myRegistration && myRegistration.registered;
    const alreadySubmitted = myRegistration && myRegistration.assessment_submitted;
    const hasAssessment = assessment && assessment.has_assessment;

    return (
      <Card className="scan-card" title={session.training_name || session.asset_name || 'Training session'}>
        {renderSessionCard()}

        {hasAssessment && (
          <Alert
            className="assessment-notice"
            type="info"
            showIcon
            icon={<FormOutlined />}
            message="This session includes an assessment"
            description={
              assessment.scored
                ? `You'll be asked to complete "${assessment.form_name}". A score of ${assessment.pass_mark}% or above is a pass.`
                : `You'll be asked to complete "${assessment.form_name}". Your trainer confirms your competency afterwards.`
            }
          />
        )}

        {alreadyRegistered && (
          <Alert
            className="assessment-notice"
            type="success"
            showIcon
            message="You're already on the attendee list"
            description={
              alreadySubmitted
                ? 'You have already completed the assessment for this session.'
                : hasAssessment
                ? 'You still need to complete the assessment.'
                : 'Nothing else to do — enjoy the session.'
            }
          />
        )}

        <div className="scan-actions">
          {!alreadyRegistered && (
            <Button type="primary" size="large" block loading={busy} icon={<UserAddOutlined />} onClick={handleRegister}>
              {hasAssessment ? 'Register and start assessment' : 'Register my attendance'}
            </Button>
          )}
          {alreadyRegistered && hasAssessment && (
            <Button type="primary" size="large" block loading={busy} icon={<FormOutlined />} onClick={openAssessment}>
              {alreadySubmitted ? 'Retake the assessment' : 'Start the assessment'}
            </Button>
          )}
        </div>
      </Card>
    );
  };

  const renderAssessment = () => (
    <Card className="scan-card" title={assessmentForm.name}>
      {previousAttempt && (
        <Alert
          className="assessment-notice"
          type="warning"
          showIcon
          message="You have already sat this assessment"
          description={
            previousAttempt.percentage === null
              ? 'Submitting again replaces your previous answers.'
              : `Your last attempt scored ${previousAttempt.percentage}% (${previousAttempt.result}). Submitting again replaces it.`
          }
        />
      )}
      <SchemaFormRenderer form={assessmentForm} loading={busy} onSubmit={handleSubmitAssessment} />
    </Card>
  );

  const renderDone = () => {
    // No scoring block means the assessment was evidence-only (or there was no
    // assessment at all) — say what actually happens next rather than inventing
    // a pass the trainer hasn't given.
    if (!scoring || !scoring.scored) {
      return (
        <Result
          status="success"
          title="You're registered"
          subTitle={
            scoring
              ? 'Your answers have been recorded. Your trainer will confirm your competency.'
              : 'Your attendance has been recorded for this session.'
          }
        />
      );
    }

    const passed = scoring.result === 'pass';
    return (
      <Result
        status={passed ? 'success' : 'warning'}
        icon={passed ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
        title={passed ? 'Assessment passed' : 'Not yet competent'}
        subTitle={
          passed
            ? 'Your result has been recorded against this training.'
            : 'Your trainer will go through this with you. You can sit the assessment again.'
        }
      >
        <div className="score-panel">
          <Progress
            type="circle"
            percent={scoring.percentage}
            status={passed ? 'success' : 'exception'}
            format={(percent) => `${percent}%`}
          />
          <div className="score-facts">
            <div>
              <span className="label">Score</span>
              <span className="value">{scoring.score} / {scoring.max}</span>
            </div>
            <div>
              <span className="label">Pass mark</span>
              <span className="value">{scoring.pass_mark}%</span>
            </div>
            <div>
              <span className="label">Result</span>
              <Tag color={passed ? 'green' : 'red'}>{passed ? 'Pass' : 'Fail'}</Tag>
            </div>
          </div>
        </div>
      </Result>
    );
  };

  return (
    <LayoutWrapper>
      <TrainingSessionRegisterWrapper>
        {phase === PHASE.LOADING && (
          <div className="scan-loading">
            <Spin size="large" tip="Loading session..." />
          </div>
        )}

        {phase === PHASE.ERROR && (
          <Result
            status="error"
            title="Session unavailable"
            subTitle={errorMessage}
            extra={<Button onClick={loadSession}>Try again</Button>}
          />
        )}

        {phase === PHASE.CONFIRM && session && renderConfirm()}
        {phase === PHASE.ASSESSMENT && assessmentForm && renderAssessment()}
        {phase === PHASE.DONE && renderDone()}
      </TrainingSessionRegisterWrapper>
    </LayoutWrapper>
  );
};

export default TrainingSessionRegister;
